import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Activity } from 'lucide-react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  ResponsiveContainer, Tooltip,
} from 'recharts';
import { API } from '@/lib/api';
import { cn } from '@/lib/utils';

const SINTOMAS = [
  { key: 'energia',   label: 'Energía',   emoji: '⚡' },
  { key: 'digestion', label: 'Digestión', emoji: '🌿' },
  { key: 'hambre',    label: 'Hambre',    emoji: '🍽️' },
  { key: 'sueno',     label: 'Sueño',     emoji: '😴' },
  { key: 'animo',     label: 'Ánimo',     emoji: '😊' },
];

const COLOR_DOT = (val) => {
  if (!val) return 'bg-muted';
  if (val >= 4) return 'bg-emerald-500';
  if (val >= 3) return 'bg-amber-400';
  return 'bg-rose-500';
};

const ScoreInput = ({ label, emoji, value, onChange }) => (
  <div className="space-y-2">
    <p className="text-xs font-medium">{emoji} {label}</p>
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === value ? null : n)}
          className={cn(
            'flex-1 h-9 rounded-lg border text-sm font-medium transition-colors',
            value === n
              ? n >= 4 ? 'bg-emerald-500 text-white border-emerald-500'
                : n === 3 ? 'bg-amber-400 text-white border-amber-400'
                : 'bg-rose-500 text-white border-rose-500'
              : 'border-border hover:bg-muted'
          )}
        >
          {n}
        </button>
      ))}
    </div>
    <p className="text-xs text-muted-foreground">1 = bajo &nbsp;·&nbsp; 5 = alto</p>
  </div>
);

const emptyForm = {
  fecha: new Date().toISOString().split('T')[0],
  energia: null, digestion: null, hambre: null, sueno: null, animo: null,
  notas: '',
};

export const BitacoraSintomas = ({ patient }) => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetch = async () => {
    try {
      const res = await axios.get(`${API}/patients/${patient.id}/sintomas`);
      setRegistros(res.data);
    } catch { toast.error('Error al cargar bitácora'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [patient.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fecha) { toast.error('La fecha es obligatoria'); return; }
    const payload = {
      paciente_id: patient.id,
      ...form,
      notas: form.notas || null,
    };
    try {
      await axios.post(`${API}/patients/${patient.id}/sintomas`, payload);
      toast.success('Registro guardado');
      setDialogOpen(false);
      setForm(emptyForm);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este registro? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/patients/${patient.id}/sintomas/${id}`);
      setRegistros(prev => prev.filter(r => r.id !== id));
      toast.success('Registro eliminado');
    } catch { toast.error('Error al eliminar'); }
  };

  // Último registro para radar
  const latest = registros[0];
  const radarData = latest
    ? SINTOMAS.map(s => ({ subject: s.label, value: latest[s.key] ?? 0, fullMark: 5 }))
    : null;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setForm(emptyForm); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" />
          Nuevo registro
        </Button>
      </div>

      {/* Radar del último registro */}
      {radarData && (
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Última consulta — {latest.fecha}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <ResponsiveContainer width={220} height={200}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                  <Radar dataKey="value" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                  <Tooltip formatter={(v) => [`${v}/5`]} />
                </RadarChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-3 flex-1">
                {SINTOMAS.map(s => (
                  <div key={s.key} className="flex items-center gap-2">
                    <span className="text-base">{s.emoji}</span>
                    <div>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                      <div className="flex gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map(n => (
                          <div
                            key={n}
                            className={cn(
                              'h-2 w-4 rounded-full',
                              n <= (latest[s.key] ?? 0) ? COLOR_DOT(latest[s.key]) : 'bg-muted'
                            )}
                          />
                        ))}
                        <span className="text-xs ml-1 font-medium">{latest[s.key] ?? '—'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {latest.notas && (
              <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/40">{latest.notas}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Historial */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Historial</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-6">Cargando...</p>
          ) : registros.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <Activity className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">Sin registros de síntomas</p>
            </div>
          ) : (
            <div className="space-y-2">
              {registros.map(r => (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors">
                  <span className="text-xs text-muted-foreground w-20 shrink-0">{r.fecha}</span>
                  <div className="flex gap-3 flex-1">
                    {SINTOMAS.map(s => r[s.key] != null && (
                      <div key={s.key} className="flex items-center gap-1 text-xs">
                        <span>{s.emoji}</span>
                        <span className={cn(
                          'font-medium',
                          r[s.key] >= 4 ? 'text-emerald-500'
                            : r[s.key] >= 3 ? 'text-amber-500'
                            : 'text-rose-500'
                        )}>{r[s.key]}</span>
                      </div>
                    ))}
                  </div>
                  {r.notas && <p className="text-xs text-muted-foreground truncate max-w-32">{r.notas}</p>}
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 hover:text-destructive"
                    onClick={() => handleDelete(r.id)} aria-label="Eliminar registro">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registro de síntomas</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label>Fecha *</Label>
              <Input type="date" value={form.fecha}
                onChange={e => setForm({ ...form, fecha: e.target.value })} />
            </div>
            {SINTOMAS.map(s => (
              <ScoreInput
                key={s.key}
                label={s.label}
                emoji={s.emoji}
                value={form[s.key]}
                onChange={val => setForm({ ...form, [s.key]: val })}
              />
            ))}
            <div className="space-y-1">
              <Label>Notas</Label>
              <Textarea rows={2} placeholder="Observaciones..." value={form.notas}
                onChange={e => setForm({ ...form, notas: e.target.value })} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="flex-1">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
